import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress
} from "@material-ui/core";
import { Phone } from "@material-ui/icons";
import QRCode from "qrcode.react";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

/**
 * gallera-voice FASE 2 — botão "Ativar Gallera Voz" por conexão + modal de QR.
 *
 * Auto-gated: só aparece se GET /voice/enabled = true (plano useVoice + serviço
 * configurado no servidor). Ao clicar, garante a sessão de voz do número
 * (POST /voice/:id/session) e abre um modal com o QR (GET /voice/:id/qr) para o
 * cliente parear o "dispositivo adicional" de voz daquele WhatsApp. Faz polling
 * do status até parear (state PAIRED/paired=true) e então fecha sozinho.
 *
 * Componente isolado p/ não engrossar a página de Conexões (2k+ linhas): guarda
 * seu próprio estado, dialog e polling.
 */
const GalleraVoiceConnectionButton = ({ whatsapp }) => {
  const [available, setAvailable] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [qr, setQr] = useState("");
  const [paired, setPaired] = useState(false);
  const [linkInfo, setLinkInfo] = useState(null); // modelo por token (.254)
  const pollRef = useRef(null);

  // Modelo Wavoip: a conexão está vinculada à voz quando tem um Token de voz colado.
  const hasToken = !!String(whatsapp?.voiceToken || "").trim();

  useEffect(() => {
    let alive = true;
    api
      .get("/voice/enabled")
      .then(({ data }) => { if (alive) setAvailable(!!data?.enabled); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const refreshStatus = useCallback(async () => {
    try {
      const { data } = await api.get(`/voice/${whatsapp.id}/status`);
      if (data?.paired || String(data?.state || "").toUpperCase() === "PAIRED") {
        setPaired(true);
        setQr("");
        stopPoll();
        toast.success("Gallera Voz pareada nesta conexão!");
        return;
      }
      if (data?.qr) setQr(data.qr);
    } catch (_) {
      /* silencioso durante polling */
    }
  }, [whatsapp.id, stopPoll]);

  const handleOpen = useCallback(async () => {
    setOpen(true);
    setLoading(true);
    setPaired(false);
    setQr("");
    setLinkInfo(null);
    try {
      // Modelo por TOKEN (.254): o pareamento vive no painel de voz. Aqui só validamos
      // o vínculo e mostramos o estado (não há QR local).
      if (hasToken) {
        await api.post(`/voice/${whatsapp.id}/session`); // valida o token no painel
        const { data } = await api.get(`/voice/${whatsapp.id}/config`);
        setLinkInfo(data || { enabled: false });
        if (data?.enabled) toast.success("Voz vinculada a esta conexão!");
        return;
      }
      // Legado (motor local): garante a sessão + polling de QR.
      await api.post(`/voice/${whatsapp.id}/session`);
      await refreshStatus();
      stopPoll();
      pollRef.current = setInterval(refreshStatus, 2500);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [whatsapp.id, hasToken, refreshStatus, stopPoll]);

  const handleClose = useCallback(() => {
    stopPoll();
    setOpen(false);
  }, [stopPoll]);

  useEffect(() => () => stopPoll(), [stopPoll]);

  if (!available) return null;

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="primary"
        startIcon={<Phone />}
        style={{ marginLeft: 8 }}
        onClick={handleOpen}
      >
        {hasToken ? "Voz (verificar vínculo)" : "Ativar Gallera Voz"}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="xs">
        <DialogTitle>Gallera Voz — {whatsapp.name}</DialogTitle>
        <DialogContent style={{ textAlign: "center", minWidth: 300 }}>
          {loading && <CircularProgress />}
          {/* Modelo por TOKEN (.254): mostra o estado do vínculo, sem QR local */}
          {!loading && hasToken && (
            <Typography style={{ padding: 20 }}>
              {linkInfo?.enabled ? (
                <>
                  ✅ Voz vinculada a esta conexão (painel de voz).
                  <br />
                  Estado: <b>{linkInfo.state || "—"}</b>
                  {linkInfo.jid ? (
                    <>
                      <br />
                      Número: <b>{String(linkInfo.jid).split("@")[0]}</b>
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  ⚠️ Token de voz não validou no painel. Confira se o token colado
                  está correto e se o painel de voz está acessível.
                </>
              )}
            </Typography>
          )}
          {!loading && !hasToken && paired && (
            <Typography style={{ padding: 20 }}>
              ✅ Voz pareada nesta conexão. As ligações agora chegam no painel.
            </Typography>
          )}
          {!loading && !hasToken && !paired && qr && (
            <>
              <Typography variant="body2" style={{ marginBottom: 12 }}>
                Abra o WhatsApp do número, vá em <b>Aparelhos conectados</b> e
                escaneie o código abaixo para ativar a voz.
              </Typography>
              <QRCode value={qr} size={256} level="M" includeMargin />
            </>
          )}
          {!loading && !hasToken && !paired && !qr && (
            <Typography style={{ padding: 20 }}>
              Aguardando o código de pareamento do motor de voz...
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GalleraVoiceConnectionButton;
